import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text, Button, Group, TextInput, Modal, Stack, Table, ActionIcon, Tooltip, Badge, Center, Loader, Checkbox, Divider, ScrollArea, Card, Menu } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useBreakpoint } from '../../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { IconPlus, IconEdit, IconTrash, IconSearch, IconTemplate, IconCode, IconListCheck, IconDotsVertical } from '@tabler/icons-react'
import { getCheckTemplates, createCheckTemplate, updateCheckTemplate, deleteCheckTemplate, getSoftwareLibrary, getChecklistItems } from '../../api/client'

export default function CheckTemplatesTab() {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const [templates, setTemplates] = useState<any[]>([])
  const [swLib, setSwLib] = useState<any[]>([])
  const [chkLib, setChkLib] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [modalLoading, setModalLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [opened, { open, close }] = useDisclosure(false)
  const [editing, setEditing] = useState<any>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ name: '', department: '', description: '', softwareItems: [] as any[], checklistItems: [] as any[] })

  const loadTemplates = async () => {
    setLoading(true)
    try { const r = await getCheckTemplates(); setTemplates(r.data ?? []) }
    catch {} finally { setLoading(false) }
  }

  const loadLibraries = async () => {
    setModalLoading(true)
    try {
      const [sw, chk] = await Promise.all([getSoftwareLibrary(), getChecklistItems()])
      const swData = sw.data ?? []
      const chkData = chk.data ?? []
      setSwLib(swData)
      setChkLib(chkData)
      return { swData, chkData }
    } catch {
      return { swData: [] as any[], chkData: [] as any[] }
    } finally { setModalLoading(false) }
  }

  useEffect(() => { loadTemplates() }, [])

  const openCreate = async () => {
    setEditing(null)
    setForm({ name: '', department: '', description: '', softwareItems: [], checklistItems: [] })
    open()
    await loadLibraries()
  }

  const openEdit = async (t: any) => {
    setEditing(t)
    setForm({ name: t.name, department: t.department ?? '', description: t.description ?? '', softwareItems: t.softwareItems ?? [], checklistItems: t.checklistItems ?? [] })
    open()
    const { swData, chkData } = await loadLibraries()
    // ── Reconcile against live library — drop entries pointing at deleted items ──
    // (a saved template is a JSON snapshot; if a checklist/software item was
    // later deleted from the library, it stays in the snapshot but has no
    // matching checkbox to render, silently inflating the "selected" count)
    const liveSwIds  = new Set(swData.map((s: any) => s.id))
    const liveChkIds = new Set(chkData.map((c: any) => c.id))
    setForm(p => ({
      ...p,
      softwareItems: p.softwareItems.filter((s: any) => liveSwIds.has(s.softwareId)),
      checklistItems: p.checklistItems.filter((c: any) => liveChkIds.has(c.checklistItemId)),
    }))
  }

  const toggleSw = (sw: any) => {
    setForm(p => {
      const exists = p.softwareItems.find((s: any) => s.softwareId === sw.id)
      if (exists) return { ...p, softwareItems: p.softwareItems.filter((s: any) => s.softwareId !== sw.id) }
      return { ...p, softwareItems: [...p.softwareItems, { softwareId: sw.id, name: sw.name, required: true, checkVersion: sw.checkVersion ?? true }] }
    })
  }

  const toggleSwRequired = (swId: number) => {
    setForm(p => ({ ...p, softwareItems: p.softwareItems.map((s: any) => s.softwareId === swId ? { ...s, required: !s.required } : s) }))
  }

  const toggleChk = (chk: any) => {
    setForm(p => {
      const exists = p.checklistItems.find((c: any) => c.checklistItemId === chk.id)
      if (exists) return { ...p, checklistItems: p.checklistItems.filter((c: any) => c.checklistItemId !== chk.id) }
      return { ...p, checklistItems: [...p.checklistItems, { checklistItemId: chk.id, name: chk.name, required: true, requiresNote: chk.requiresNote ?? false }] }
    })
  }

  const toggleChkRequired = (chkId: number) => {
    setForm(p => ({ ...p, checklistItems: p.checklistItems.map((c: any) => c.checklistItemId === chkId ? { ...c, required: !c.required } : c) }))
  }

  const save = async () => {
    if (!form.name.trim()) { notifications.show({ color: 'red', message: 'Please enter a Template name' }); return }
    setSaving(true)
    try {
      editing ? await updateCheckTemplate(editing.id, form) : await createCheckTemplate(form)
      notifications.show({ color: 'teal', message: 'Saved successfully' })
      close(); loadTemplates()
    } catch { notifications.show({ color: 'red', message: 'An error occurred' }) }
    finally { setSaving(false) }
  }

  const handleDelete = (t: any) => modals.openConfirmModal({
    title: 'Delete Template', children: <Text size="sm">Confirm delete <b>{t.name}</b>?</Text>,
    labels: { confirm: 'Delete', cancel: 'Cancel' }, confirmProps: { color: 'red' },
    onConfirm: async () => { await deleteCheckTemplate(t.id); loadTemplates() },
  })

  const filtered = templates.filter(t => !search || t.name?.toLowerCase().includes(search.toLowerCase()))

  return (
    <Box>
      <Group justify="space-between" mb="md" wrap="wrap">
        <Group gap={8}><IconTemplate size={18} color="#10b981" /><Text fw={600}>Check Templates <Badge color="teal" variant="light" size="sm">{templates.length}</Badge></Text></Group>
        <Group gap={8} wrap="wrap">
          <TextInput size="xs" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} leftSection={<IconSearch size={13} />} style={{ width: isMobile ? '100%' : 200 }} />
          <Button size="xs" leftSection={<IconPlus size={13} />} color="teal" onClick={openCreate}>{t('common.create', 'Create')} Template</Button>
        </Group>
      </Group>

      <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
        {loading ? <Center p="xl"><Loader size="sm" color="teal" /></Center> : isMobile ? (
          filtered.length === 0 ? (
            <Center p="xl"><Text size="sm" c="dimmed">No Template yet</Text></Center>
          ) : (
            <Stack gap="xs" p="xs">
              {filtered.map(tpl => (
                <Card key={tpl.id} withBorder radius="lg" p="md">
                  <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Text size="sm" fw={600}>{tpl.name}</Text>
                      {tpl.description && <Text size="xs" c="dimmed" lineClamp={1}>{tpl.description}</Text>}
                      <Text size="xs" c="dimmed" mt={2}>{tpl.department || 'General'}</Text>
                      <Group gap={6} mt={4}>
                        <Badge color="blue" variant="light" size="xs">{(tpl.softwareItems ?? []).length} SW</Badge>
                        <Badge color="violet" variant="light" size="xs">{(tpl.checklistItems ?? []).length} Checklist</Badge>
                      </Group>
                    </Box>
                    <Menu shadow="md" width={160} position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" size="lg" style={{ flexShrink: 0 }}><IconDotsVertical size={18} /></ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item leftSection={<IconEdit size={15} />} onClick={() => openEdit(tpl)}>{t('common.edit', 'Edit')}</Menu.Item>
                        <Menu.Item leftSection={<IconTrash size={15} />} color="red" onClick={() => handleDelete(tpl)}>{t('common.delete', 'Delete')}</Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
              ))}
            </Stack>
          )
        ) : (
          <Table highlightOnHover>
            <Table.Thead style={{ background: '#f8fafc' }}>
              <Table.Tr>
                {['Template Name', 'Department', 'Software', 'Checklist', ''].map(h => (
                  <Table.Th key={h} style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase' }}>{h}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.length === 0
                ? <Table.Tr><Table.Td colSpan={5}><Center p="xl"><Text size="sm" c="dimmed">No Template yet</Text></Center></Table.Td></Table.Tr>
                : filtered.map(tpl => (
                  <Table.Tr key={tpl.id}>
                    <Table.Td><Text size="sm" fw={600}>{tpl.name}</Text>{tpl.description && <Text size="xs" c="dimmed">{tpl.description}</Text>}</Table.Td>
                    <Table.Td><Text size="sm">{tpl.department || 'General'}</Text></Table.Td>
                    <Table.Td><Badge color="blue" variant="light" size="sm">{(tpl.softwareItems ?? []).length} item(s)</Badge></Table.Td>
                    <Table.Td><Badge color="violet" variant="light" size="sm">{(tpl.checklistItems ?? []).length} item(s)</Badge></Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end">
                        <Tooltip label={t('common.edit', 'Edit')}><ActionIcon size="sm" variant="subtle" color="blue" onClick={() => openEdit(tpl)}><IconEdit size={14} /></ActionIcon></Tooltip>
                        <Tooltip label={t('common.delete', 'Delete')}><ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDelete(tpl)}><IconTrash size={14} /></ActionIcon></Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
            </Table.Tbody>
          </Table>
        )}
      </Box>

      <Modal opened={opened} onClose={close} title={<Text fw={600}>{editing ? 'Edit Template' : 'Create Template'}</Text>} size="xl" radius="lg" fullScreen={isMobile}>
        <Stack gap="md">
          <Group grow wrap="wrap">
            <TextInput label="Template Name *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Front Desk Standard" />
            <TextInput label="Department (optional)" value={form.department} onChange={e => setForm(p => ({ ...p, department: e.target.value }))} placeholder="e.g. IT, Engineering" />
          </Group>
          <TextInput label="Description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />

          <Divider label={<Group gap={4}><IconCode size={13} /><Text size="xs" fw={600}>Software to Check ({form.softwareItems.length} selected)</Text></Group>} labelPosition="left" />

          {modalLoading ? <Center p="md"><Loader size="sm" color="teal" /></Center> : (
            <ScrollArea h={200} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px' }}>
              {swLib.length === 0
                ? <Text size="sm" c="dimmed" ta="center" mt="md">No Software in Library — please add to SW Library first</Text>
                : <Stack gap={8}>
                  {swLib.map(sw => {
                    const sel = form.softwareItems.find((s: any) => s.softwareId === sw.id)
                    return (
                      <Group key={sw.id} gap={10} wrap="nowrap">
                        <Checkbox size="sm" checked={!!sel} onChange={() => toggleSw(sw)}
                          label={
                            <Group gap={6}>
                              <Text size="sm">{sw.name}</Text>
                              {sw.currentVersion && <Badge size="xs" variant="outline" color="gray">v{sw.currentVersion}</Badge>}
                            </Group>
                          }
                        />
                        {sel && (
                          <Badge size="xs" color={sel.required ? 'red' : 'gray'} variant="light" style={{ cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => toggleSwRequired(sw.id)}>
                            {sel.required ? 'Required' : 'Optional'}
                          </Badge>
                        )}
                      </Group>
                    )
                  })}
                </Stack>
              }
            </ScrollArea>
          )}

          <Divider label={<Group gap={4}><IconListCheck size={13} /><Text size="xs" fw={600}>Checklist Actions ({form.checklistItems.length} selected)</Text></Group>} labelPosition="left" />

          {modalLoading ? null : (
            <ScrollArea h={200} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px' }}>
              {chkLib.length === 0
                ? <Text size="sm" c="dimmed" ta="center" mt="md">No Checklist Item — please add to Checklist Items first</Text>
                : <Stack gap={8}>
                  {chkLib.map(chk => {
                    const sel = form.checklistItems.find((c: any) => c.checklistItemId === chk.id)
                    return (
                      <Group key={chk.id} gap={10} wrap="nowrap">
                        <Checkbox size="sm" checked={!!sel} onChange={() => toggleChk(chk)}
                          label={
                            <Group gap={6}>
                              <Text size="sm">{chk.name}</Text>
                              {chk.category && <Badge size="xs" variant="outline" color="violet">{chk.category}</Badge>}
                            </Group>
                          }
                        />
                        {sel && (
                          <Badge size="xs" color={sel.required ? 'red' : 'gray'} variant="light" style={{ cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => toggleChkRequired(chk.id)}>
                            {sel.required ? 'Required' : 'Optional'}
                          </Badge>
                        )}
                      </Group>
                    )
                  })}
                </Stack>
              }
            </ScrollArea>
          )}

          <Group justify="space-between" mt="sm" wrap="wrap">
            <Text size="xs" c="dimmed">Click the "Required/Optional" badge to toggle status</Text>
            <Group gap={8}>
              <Button variant="default" onClick={close}>{t('common.cancel', 'Cancel')}</Button>
              <Button color="teal" loading={saving} onClick={save}>{editing ? 'Save' : 'Create'}</Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
